import React, { useState, useRef, useEffect } from "react";
import mapboxgl from "!mapbox-gl"; // eslint-disable-line import/no-webpack-loader-syntax
import { MapAction } from "../map-action";
import $ from "jquery";
import provinces from "./provinces.json";
import { MAPBOX_ACCESS_TOKEN } from "app/constant/mapbox";
import { DEFAULT_ICON } from "../../../../../assets/icon/default-icon/marker";
import styles from "./map-content.style.module.css";

mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;

const INITIAL_ZOOM = 7;
const FOCUS_ZOOM = 15;
const STREET_MODE = 1;
const SATELLITE_MODE = 2;

export const MapContent = (props) => {
  const { intl, style = 1, initialPos, markers, language, layers } = props;
  const mapContainerRef = useRef(null);
  const [globalMap, setGlobalMap] = useState();
  const [activeStyle, setActiveStyle] = useState(style);
  const [currentPos, setCurrentPos] = useState([]);

  const flyToCurrentPos = () => {
    if (currentPos?.length > 0) {
      globalMap.flyTo({
        center: currentPos,
        zoom: FOCUS_ZOOM,
        essential: true,
      });
    }
  };
  // console.log(markers);
  const getStyle = (styleId) => {
    // eslint-disable-next-line default-case
    switch (styleId) {
      case STREET_MODE:
        return process.env.REACT_APP_MAPBOX_STREET_MAP_URL;

      case SATELLITE_MODE:
        return process.env.REACT_APP_MAPBOX_SATELLITE_MAP_URL;
    }
  };

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(function (position) {
        const lng = position.coords.longitude;
        const lat = position.coords.latitude;

        setCurrentPos([lng, lat]);
      });
    }
  }, []);

  useEffect(() => {
    if (globalMap) {
      const newStyle = getStyle(style);

      globalMap?.setStyle(newStyle);
      
      // Re-add layers after style change
      globalMap.once('styledata', () => {
        addLayersToMap(globalMap, layers);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [style]);

  const addLayersToMap = (map, layers) => {
    if (layers?.length > 0) {
      
      layers?.forEach((layer, index) => {
        
        // Skip shapefile layers that don't have converted GeoJSON
        if (layer?.data_type === 'shapefile' && !layer?.geo_json_url) {
          return;
        }
        
        // For KML files, prefer geo_json_url (converted version)
        // For shapefile, we should use geo_json_url if available 
        // For geojson, use data_file_url
        let dataUrl = layer?.url;
        
        if (layer?.data_type === 'kml' && layer?.geo_json_url) {
          dataUrl = `${process.env.REACT_APP_API_URL}/${layer.geo_json_url}`;
        } else if (layer?.data_type === 'shapefile' && layer?.geo_json_url) {
          dataUrl = `${process.env.REACT_APP_API_URL}/${layer.geo_json_url}`;
        }
        

        // Add error handling for source
        try {
          // Check if source already exists
          if (map.getSource(`source-${layer?.id}`)) {
            map.removeSource(`source-${layer?.id}`);
          }

          map.addSource(`source-${layer?.id}`, {
            type: "geojson",
            data: dataUrl,
          });

          // Add layers with explicit beforeId to ensure they appear on top
          const beforeId = map.getStyle().layers.find(l => l.id === 'waterway-label') ? 'waterway-label' : undefined;

          map.addLayer({
            id: `fill-layer-${layer?.id}`,
            type: "fill",
            source: `source-${layer?.id}`,
            filter: ["==", "$type", "Polygon"],
            paint: {
              "fill-color": "#ff0000",
              "fill-opacity": 0.3,
            },
          }, beforeId);

          map.addLayer({
            id: `line-layer-${layer?.id}`,
            type: "line",
            source: `source-${layer?.id}`,
            filter: ["in", "$type", "Polygon", "LineString"],
            paint: {
              "line-color": "#4285F4",
              "line-width": 2,
            },
          }, beforeId);

          map.addLayer({
            id: `circle-layer-${layer?.id}`,
            type: "circle",
            source: `source-${layer?.id}`,
            filter: ["==", "$type", "Point"],
            paint: {
              "circle-radius": 8,
              "circle-stroke-width": 2,
              "circle-color": "#ff0000",
              "circle-stroke-color": "#ffffff",
            },
          }, beforeId);

          
          // Try to fit map to show the layer data
          map.on('sourcedata', function(e) {
            if (e.sourceId === `source-${layer?.id}` && e.isSourceLoaded) {
              try {
                const source = map.getSource(`source-${layer?.id}`);
                if (source && source._data && source._data.features && source._data.features.length > 0) {
                  console.log(`Layer ${layer?.id} has ${source._data.features.length} features`);
                }
              } catch (error) {
                console.log(`Could not get feature count for layer ${layer?.id}`);
              }
            }
          });
          
        } catch (error) {
          console.error(`Error adding layer ${layer?.id}:`, error);
        }
      });
    }
  };

  useEffect(() => {
    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: getStyle(activeStyle),
      center: [initialPos?.[1], initialPos?.[0]],
      zoom: INITIAL_ZOOM,
      minZoom: 4,
      maxBounds: [
        [-180, -85],
        [180, 85],
      ],
    });

    map.addControl(new mapboxgl.NavigationControl());

    map.on("load", function () {
      map.loadImage("/images/current-position.png", function (error, image) {
        map.addImage(`current-position`, image, {
          width: 24,
          height: 24,
        });
      });

      markers?.forEach((marker) => {
        const iconUrl = marker?.point_marker
          ? new Buffer.from(marker?.point_marker?.data).toString("base64")
          : DEFAULT_ICON;

        map.loadImage(`data:;base64,${iconUrl}`, function (error, image) {
          map.addImage(`image-${marker?.id}`, image, {
            width: 24,
            height: 24,
          });
        });
      });

      // Layer bản đồ Hoàng Sa, Trường Sa
      map.addSource(`source-0`, {
        type: "geojson",
        data: provinces,
      });

      map.addLayer({
        id: `line-layer-0`,
        type: "line",
        source: `source-0`,
        paint: {
          "line-color": "#aaa",
          "line-width": 1,
        },
      });

      // Add custom layers
      addLayersToMap(map, layers);

      let timer = setTimeout(() => {
        let features = markers?.map((marker) => {
          return {
            type: "Feature",
            properties: {
              title: language === "en" ? marker?.name : marker?.name_vi,
              image: `image-${marker?.id}`,
            },
            geometry: {
              coordinates: [
                Number(marker?.longtitude),
                Number(marker?.latitude),
              ],
              type: "Point",
            },
          };
        });

        map.addSource("points", {
          type: "geojson",
          data: {
            type: "FeatureCollection",
            features: features,
          },
        });

        map.addLayer({
          id: "points",
          type: "symbol",
          source: "points",
          layout: {
            "icon-image": ["get", "image"],
            "icon-size": 0.1,
            "text-field": ["get", "title"] || "",
            "text-font": ["Open Sans Semibold", "Arial Unicode MS Bold"],
            "text-offset": [0, 2],
            "text-anchor": "top",
          },
          paint: {
            "text-color": activeStyle === 1 ? "#000" : "#fff",
          },
        });

        navigator?.geolocation?.getCurrentPosition((position) => {
          if (currentPos?.length > 0) {
            features.push({
              type: "Feature",
              properties: {
                image: "current-position",
              },
              geometry: {
                coordinates: currentPos,
                type: "Point",
              },
            });
          }
        });

        clearTimeout(timer);
      }, 1000);
    });

    setGlobalMap(map);

    return () => map.remove();
  }, [style, markers, language, activeStyle, layers]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={styles.container} ref={mapContainerRef}>
      <div className={styles.innerContainer}>
        <MapAction
          intl={intl}
          onLocateMyLocation={flyToCurrentPos}
          onChangeLayer={(style) => setActiveStyle(style)}
          onZoom={(zoomLevel) => {
            if (zoomLevel === "In") {
              $(".mapboxgl-ctrl-zoom-in").click();
            } else {
              $(".mapboxgl-ctrl-zoom-out").click();
            }
          }}
        />
      </div>
    </div>
  );
};
